import * as React from "react";
import {
  RadialBarChart as RechartsRadialBarChart,
  RadialBar,
  PolarRadiusAxis,
  Label,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "./chart";
import { cn } from "@/lib/utils";

export interface RadialChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?: "default" | "animated" | "interactive" | "minimal" | "stacked" | "text" | "shape";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  dataKeys?: string[];
  colors?: string[];
  strokeWidth?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  showLabels?: boolean;
  height?: number | string;
  width?: number | string;
  innerRadius?: number | string;
  outerRadius?: number | string;
  startAngle?: number;
  endAngle?: number;
  cornerRadius?: number;
  background?: boolean;
  centerLabel?: {
    value: string | number;
    label?: string;
    fontSize?: number;
    fontWeight?: string;
  };
}

export function RadialChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  dataKeys = ["value"],
  colors = [
    "hsl(221.2 83.2% 53.3%)",
    "hsl(217.2 91.2% 59.8%)",
    "hsl(214.3 31.8% 91.4%)",
    "hsl(210 40% 96%)",
  ],
  strokeWidth = 2,
  showLegend = false,
  showTooltip = true,
  showLabels = false,
  height,
  width,
  innerRadius = "20%",
  outerRadius = "80%",
  startAngle = 0,
  endAngle = 360,
  cornerRadius = 4,
  background = false,
  centerLabel,
  ...props
}: RadialChartProps) {
  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return [
          "#60a5fa",
          "#34d399",
          "#fbbf24",
          "#f87171",
        ];
      case "minimal":
        return [
          "#6b7280",
          "#9ca3af",
          "#d1d5db",
          "#e5e7eb",
        ];
      default:
        return colors;
    }
  };

  const themeColors = getThemeColors();

  // Determine variant-specific props
  const getVariantProps = () => {
    switch (variant) {
      case "animated":
        return {
          className: cn(className, "wl-radial-chart-animated"),
          animationDuration: 1500,
          animationEasing: "ease-out",
        };
      case "interactive":
        return {
          className: cn(className, "wl-radial-chart-interactive"),
        };
      case "minimal":
        return {
          className: cn(className, "wl-radial-chart-minimal"),
          strokeWidth: 1,
          cornerRadius: 0,
        };
      case "stacked":
        return {
          className: cn(className, "wl-radial-chart-stacked"),
        };
      case "text":
        return {
          className: cn(className, "wl-radial-chart-text"),
          showLabels: true,
        };
      case "shape":
        return {
          className: cn(className, "wl-radial-chart-shape"),
          cornerRadius: 0,
        };
      default:
        return {
          className,
        };
    }
  };

  const variantProps = getVariantProps();

  // Get corner radius based on variant
  const getCornerRadius = () => {
    if (variant === "minimal" || variant === "shape") return 0;
    return cornerRadius;
  };

  // Get stroke width based on variant
  const getStrokeWidth = () => {
    if (variant === "minimal") return 1;
    return strokeWidth;
  };

  // Center label component
  const CenterLabel = ({ viewBox }: any) => {
    if (!centerLabel || !viewBox || !("cx" in viewBox) || !("cy" in viewBox)) {
      return null;
    }

    return (
      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
        <tspan
          x={viewBox.cx}
          y={(viewBox.cy || 0) - 16}
          className="wl-fill-foreground wl-text-2xl wl-font-bold"
          style={{
            fontSize: centerLabel.fontSize || 24,
            fontWeight: centerLabel.fontWeight || "bold",
          }}
        >
          {centerLabel.value}
        </tspan>
        {centerLabel.label && (
          <tspan
            x={viewBox.cx}
            y={(viewBox.cy || 0) + 4}
            className="wl-fill-muted-foreground"
          >
            {centerLabel.label}
          </tspan>
        )}
      </text>
    );
  };

  return (
    <div
      className={cn(
        "wl-radial-chart",
        "wl-radial-chart-theme",
        `wl-radial-chart-theme--${theme}`,
        variant !== "default" && `wl-radial-chart-${variant}`,
        responsive && "wl-radial-chart-responsive",
        accessible && "wl-radial-chart-accessible",
        className
      )}
      data-chart-type="radial"
      style={{ height, width }}
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsRadialBarChart
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
          {...variantProps}
        >
          <PolarRadiusAxis
            tick={false}
            tickLine={false}
            axisLine={false}
            type="number"
            domain={[0, 100]}
          >
            {centerLabel && <Label content={<CenterLabel />} />}
          </PolarRadiusAxis>
          
          {showTooltip && (
            <ChartTooltip
              content={<ChartTooltipContent hideLabel />}
              cursor={false}
            />
          )}
          
          {showLegend && (
            <ChartLegend content={<ChartLegendContent />} />
          )}
          
          {dataKeys.map((dataKey, index) => (
            <RadialBar
              key={dataKey}
              dataKey={dataKey}
              cornerRadius={getCornerRadius()}
              fill={themeColors[index % themeColors.length]}
              stroke={themeColors[index % themeColors.length]}
              strokeWidth={getStrokeWidth()}
              background={background}
              stackId={variant === "stacked" ? "a" : undefined}
              label={
                showLabels
                  ? {
                      position: "insideEnd",
                      fill: "#fff",
                      fontSize: 12,
                    }
                  : false
              }
              animationDuration={variant === "animated" ? 1500 : undefined}
              animationEasing={variant === "animated" ? "ease-out" : undefined}
            />
          ))}
        </RechartsRadialBarChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { RadialChart as default }; 